import { useEffect, useMemo, useRef, useState } from "react";
import {
  getOrCreateLoomAuthoringRecordIdentity,
  recordLoomAuthoringDefinition,
  type LoomAuthoringRecordIdentity,
} from "../../api/loomAuthoring";
import { LOOM_LOCAL_DEMO_PROJECT_ID } from "../../api/loomAuthoringAssistant";
import type { LoomRebuildBlocker } from "../../api/loomRebuild";
import {
  loadLoomNativeHybridAsset,
  type LoomNativeHybridAsset,
} from "./loomNativeHybridAsset";
import { loomAssetDefinitionMatchesDocument } from "./loomAuthoritativeDefinition";
import { deriveLoomExplicitTrimPreview } from "./loomExplicitTrimPreview";
import { deriveLoomGpuUnionPlan } from "./loomGpuImplicitUnion";
import { useLoomProvisionalUnionWorker } from "./useLoomProvisionalUnionWorker";
import {
  LoomNativeHybridViewer,
  type LoomNativeHybridLayers,
} from "./LoomNativeHybridViewer";
import { LoomPanelLayout } from "./LoomPanelLayout";
import { deriveLoomParameterControls, formatLoomControlPath } from "./loomParameterControls";
import {
  deriveLoomAnalyticPreviewSurface,
  mapLoomAnalyticTargetValue,
} from "./loomNativeViewerHelpers";
import type { LoomDefinitionRecordState } from "./LoomPrimitiveAuthoringPanel";
import { useLoomAuthoringHistory } from "./useLoomAuthoringHistory";
import { LoomSurfaceAppearanceControl, type LoomInspectionSurfaceKind } from "./LoomSurfaceAppearanceControl";
import type { LoomSurfaceAppearance } from "./loomViewportSurfaceMaterial";
import { LoomViewportPresentationControl } from "./LoomViewportPresentationControl";
import { LoomAuthoritativeRebuildControl } from "./LoomAuthoritativeRebuildControl";
import { useLoomAuthoritativeRebuild } from "./useLoomAuthoritativeRebuild";
import type { LoomVerifiedAircraftOccurrenceAsset } from "./loomAircraftOccurrenceAsset";
import { deriveLoomAcceptedSceneBinding, type AircraftSectionAcceptedSceneBinding } from "./loomAcceptedSceneBinding";
import { LOOM_DEFAULT_VIEWPORT_PRESENTATION, loomLayersForSurfaceStyle, type LoomViewportPresentationState } from "./loomViewportPresentation";
import { LoomControlRuntimeObserver, type LoomControlRuntimeState } from "./LoomControlRuntimeObserver";
import { LoomControlSelectionBridge } from "./LoomControlSelectionBridge";
import { LoomControlSurfaceProvider } from "./LoomControlSurfaceContext";
import type { LoomProductSectionDesign } from "./LoomProductTree";
import { LoomTaskPropertiesPanel } from "./LoomTaskPropertiesPanel";
import { LoomTaskAdvancedAuthoring } from "./LoomTaskAdvancedAuthoring";
import { LoomTaskTreePanel } from "./LoomTaskTreePanel";
import { useLoomProductSelection } from "./useLoomProductSelection";
import type { AircraftSectionTarget } from "./aircraftSectionExtractionApi";
import "./loom-authoring.css";
import "./loom-native-task-layout.css";
const DEFAULT_LAYERS: LoomNativeHybridLayers = { surface: true, parameterization: false,
  tessellation: false, sharedSeam: true, orderedPanels: false, trailingEdge: true, jacobian: false };
export interface LoomNativeWorkbenchRuntimeSnapshot {
  readonly acceptedSceneBinding?: AircraftSectionAcceptedSceneBinding;
  readonly acceptedSceneRootId: string;
  readonly authoringRevision: number;
  readonly contentStateId: string;
  readonly legacyControlPoseRevision?: string | null;
  readonly selectedControlSide?: "port" | "starboard" | "both" | null;
  readonly selectedControlSurfaceId?: string | null;
  readonly selectedNodeId: string;
  readonly selectedSectionTargetKind: AircraftSectionTarget["kind"] | null;
  readonly workspaceStateId: string;
}
interface LoomNativeHybridWorkbenchProps { branch: string; persistedProject: boolean; projectId: string; projectName: string; onRuntimeSnapshot?: (snapshot: LoomNativeWorkbenchRuntimeSnapshot) => void; onTaskRequest?: (task: "section" | "details") => void }
interface LoomSessionSectionDesign extends Omit<LoomProductSectionDesign, "state"> { readonly sourceRevision: number }
function recordStateForProject(persistedProject: boolean): LoomDefinitionRecordState { return persistedProject ? { status: "idle" } : { status: "unavailable" }; }
export function LoomNativeHybridWorkbench({ branch, persistedProject, projectId, projectName,
  onRuntimeSnapshot, onTaskRequest }: LoomNativeHybridWorkbenchProps) {
  const [asset, setAsset] = useState<LoomNativeHybridAsset | null>(null);
  const [acceptedAssembly, setAcceptedAssembly] = useState<{ asset: LoomVerifiedAircraftOccurrenceAsset; sourceRevision: number; sourceStateId: string } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [layers, setLayers] = useState<LoomNativeHybridLayers>(DEFAULT_LAYERS);
  const [parameterId, setParameterId] = useState("sweep_deg");
  const [jacobianScale, setJacobianScale] = useState(0.18);
  const [surfaceAppearance, setSurfaceAppearance] = useState<LoomSurfaceAppearance>("opaque");
  const [viewportPresentation, setViewportPresentation] = useState<LoomViewportPresentationState>(LOOM_DEFAULT_VIEWPORT_PRESENTATION);
  const [cameraFitRequest, setCameraFitRequest] = useState(0);
  const [rebuildFocusPath, setRebuildFocusPath] = useState<string | null>(null);
  const [sectionDesigns, setSectionDesigns] = useState<readonly LoomSessionSectionDesign[]>([]);
  const [controlRuntime, setControlRuntime] = useState<LoomControlRuntimeState>({
    legacyControlPoseRevision: null,
    selectedControlSide: null,
    selectedControlSurfaceId: null,
  });
  const [recordState, setRecordState] = useState<LoomDefinitionRecordState>(() => recordStateForProject(persistedProject));
  const sectionSequenceRef = useRef(0);
  const recordIdentityRef = useRef<{ projectId: string; identity: LoomAuthoringRecordIdentity } | null>(null);
  const recordAbortRef = useRef<AbortController | null>(null);
  const recordRequestRef = useRef(0);
  const authoringRuntime = useLoomAuthoringHistory(projectId);
  const {
    acceptCommand: applyAuthoringCommand,
    authoringReadOnlyReason,
    document: authoringDocument,
    history,
    preview,
    previewCommand,
    cancelPreview,
    storageStatus,
    redo,
    undo,
    workspaceHistory,
  } = authoringRuntime;
  const productSectionDesigns = useMemo<readonly LoomProductSectionDesign[]>(() => (
    sectionDesigns.map((section) => ({
      ...section,
      state: section.sourceRevision === authoringDocument.revision ? "current" : "stale",
    }))
  ), [authoringDocument.revision, sectionDesigns]);
  const { controlSelectionIntent, effectiveSelectedNodeId, productSelection, selectControl,
    selectNode, selectSection, settleControlSelectionIntent } = useLoomProductSelection(
      authoringDocument, projectId, controlRuntime.selectedControlSurfaceId,
    );
  useEffect(() => {
    sectionSequenceRef.current = 0;
    setSectionDesigns([]);
    setAcceptedAssembly(null);
  }, [projectId]);
  useEffect(() => {
    if (!rebuildFocusPath) return;
    const controls = document.querySelectorAll<HTMLElement>('[data-testid="loom-parameter-control"]');
    const control = [...controls].find((candidate) => candidate.dataset.nodeId === effectiveSelectedNodeId
      && candidate.dataset.parameterPath === rebuildFocusPath);
    control?.querySelector<HTMLInputElement>('input[type="number"]')?.focus({ preventScroll: true });
  }, [effectiveSelectedNodeId, rebuildFocusPath]);
  useEffect(() => {
    const controller = new AbortController();
    loadLoomNativeHybridAsset(undefined, (input, init) => fetch(input, {
      ...init,
      signal: controller.signal,
    })).then((loaded) => {
      setAsset(loaded);
      setParameterId(loaded.header.jacobian.parameterOrder[0] ?? "sweep_deg");
    }).catch((error: unknown) => {
      if (error instanceof Error && error.name === "AbortError") return;
      setLoadError(error instanceof Error ? error.message : "Unable to load the Loom hybrid artifact");
    });
    return () => controller.abort();
  }, []);
  useEffect(() => {
    recordAbortRef.current?.abort();
    recordRequestRef.current += 1;
    setRecordState(recordStateForProject(persistedProject));
    return () => recordAbortRef.current?.abort();
  }, [history.activeHead.revision, history.activeHead.stateId, persistedProject, projectId]);
  useEffect(() => {
    recordIdentityRef.current = null;
  }, [persistedProject, projectId]);
  const previewActive = preview !== null;
  const displayedAuthoringDocument = preview?.document ?? authoringDocument;
  const explicitTrimPreview = useMemo(
    () => asset ? deriveLoomExplicitTrimPreview(asset, displayedAuthoringDocument) : null,
    [asset, displayedAuthoringDocument],
  );
  const settledExplicitTrimPreview = useMemo(
    () => asset ? deriveLoomExplicitTrimPreview(asset, authoringDocument) : null,
    [asset, authoringDocument],
  );
  const assemblyCurrent = Boolean(acceptedAssembly && acceptedAssembly.sourceRevision === authoringDocument.revision
    && acceptedAssembly.sourceStateId === history.activeHead.stateId);
  const definitionMatch = asset ? loomAssetDefinitionMatchesDocument(asset, authoringDocument) : false;
  const legacyGeometryCurrent = Boolean(asset && (definitionMatch ?? (
    settledExplicitTrimPreview?.kind === "available"
      && settledExplicitTrimPreview.method === "reflected-pairwise-hybrid-base"
  )));
  const geometryCurrent = assemblyCurrent || legacyGeometryCurrent;
  const acceptedSceneBinding = useMemo(() => deriveLoomAcceptedSceneBinding(
    asset, legacyGeometryCurrent && !assemblyCurrent, previewActive), [assemblyCurrent, asset, legacyGeometryCurrent, previewActive]);
  const openSection = (nodeId: string, existingSectionId?: string) => {
    const source = authoringDocument.nodes.find((node) => node.id === nodeId);
    const canCreate = Boolean(productSelection.kind === "node"
      && productSelection.nodeId === nodeId && source?.type === "primitive"
      && (source.definition.primitive === "wing" || source.definition.primitive === "fuselage"));
    if (!existingSectionId && !canCreate) {
      onTaskRequest?.("section");
      return;
    }
    let sectionId = existingSectionId;
    if (!sectionId) {
      const sequence = ++sectionSequenceRef.current;
      sectionId = `section-design-${sequence}`;
      setSectionDesigns((current) => [...current, {
        id: sectionId!, label: `Section ${sequence}`, sourceNodeId: nodeId,
        sourceRevision: authoringDocument.revision,
      }]);
    }
    selectSection(nodeId, sectionId);
    onTaskRequest?.("section");
  };
  const runtimeControlSurfaceId = productSelection.kind === "control"
    && productSelection.controlSurfaceId === controlRuntime.selectedControlSurfaceId
    ? controlRuntime.selectedControlSurfaceId : null;
  const runtimeControlSide = runtimeControlSurfaceId ? controlRuntime.selectedControlSide : null;
  const selectedSectionTargetKind = useMemo<AircraftSectionTarget["kind"] | null>(() => {
    const source = authoringDocument.nodes.find((node) => node.id === productSelection.nodeId);
    if (source?.type !== "primitive") return null;
    if (source.definition.primitive === "wing") return "wing";
    return source.definition.primitive === "fuselage" ? "body" : null;
  }, [authoringDocument.nodes, productSelection.nodeId]);
  useEffect(() => onRuntimeSnapshot?.({ ...(acceptedSceneBinding ? { acceptedSceneBinding } : {}),
    acceptedSceneRootId: `loom-scene:${projectId}`, authoringRevision: authoringDocument.revision,
    contentStateId: history.activeHead.stateId,
    legacyControlPoseRevision: controlRuntime.legacyControlPoseRevision,
    selectedControlSide: runtimeControlSide,
    selectedControlSurfaceId: runtimeControlSurfaceId,
    selectedNodeId: productSelection.nodeId,
    selectedSectionTargetKind,
    workspaceStateId: workspaceHistory.activeHead.stateId,
  }), [acceptedSceneBinding, authoringDocument.revision, controlRuntime.legacyControlPoseRevision,
    history.activeHead.stateId, onRuntimeSnapshot, projectId, productSelection.nodeId,
    runtimeControlSide, runtimeControlSurfaceId, selectedSectionTargetKind,
    workspaceHistory.activeHead.stateId]);
  const authoritativeRebuild = useLoomAuthoritativeRebuild(
    persistedProject ? projectId : LOOM_LOCAL_DEMO_PROJECT_ID,
    authoringDocument, asset, !previewActive && (!geometryCurrent || (!authoringDocument.presetBinding && !assemblyCurrent)), previewActive,
  );
  useEffect(() => {
    if (authoritativeRebuild.result?.assemblyAsset
        && authoritativeRebuild.acceptedRevision === authoringDocument.revision
        && authoritativeRebuild.acceptedStateId === history.activeHead.stateId) {
      setAcceptedAssembly({ asset: authoritativeRebuild.result.assemblyAsset, sourceRevision: authoritativeRebuild.result.sourceRevision, sourceStateId: authoritativeRebuild.result.sourceStateId });
    } else if (authoritativeRebuild.result?.asset && asset !== authoritativeRebuild.result.asset
        && authoritativeRebuild.acceptedRevision === authoringDocument.revision
        && authoritativeRebuild.acceptedStateId === history.activeHead.stateId) {
      setAsset(authoritativeRebuild.result.asset);
    }
  }, [asset, authoringDocument.revision, authoritativeRebuild.acceptedRevision, authoritativeRebuild.acceptedStateId, authoritativeRebuild.result, history.activeHead.stateId]);
  const effectiveExtractionDirty = Boolean(asset && !geometryCurrent);
  const definitionPreviewActive = effectiveExtractionDirty || previewActive;
  const explicitTrimAvailable = explicitTrimPreview?.kind === "available";
  const provisionalUnionRuntime = useLoomProvisionalUnionWorker(authoringDocument, Boolean(asset && settledExplicitTrimPreview?.kind === "unavailable"));
  const provisionalUnionPreview = previewActive || explicitTrimPreview?.kind === "available" ? null : provisionalUnionRuntime.preview;
  const provisionalUnionAvailable = provisionalUnionPreview?.kind === "available";
  const gpuUnionPlan = useMemo(() => previewActive && explicitTrimPreview?.kind === "unavailable" ? deriveLoomGpuUnionPlan(displayedAuthoringDocument) : null, [displayedAuthoringDocument, explicitTrimPreview, previewActive]);
  const gpuUnionActive = gpuUnionPlan?.kind === "available" && viewportPresentation.assemblyView === "assembled" && viewportPresentation.surfaceStyle === "filled";
  const inspectionSurfaceKind: LoomInspectionSurfaceKind = explicitTrimAvailable ? "symmetric-explicit-trim" : gpuUnionActive ? "gpu-implicit-union" : provisionalUnionAvailable ? "provisional-explicit-union" : "authored-operands";
  const effectiveSurfaceAppearance: LoomSurfaceAppearance = inspectionSurfaceKind === "authored-operands" ? "opaque" : surfaceAppearance;
  const analyticPreviewSurface = useMemo(() => {
    if (!asset || !preview || !geometryCurrent || !preview.analyticJacobianParameterId
        || explicitTrimPreview?.kind !== "available") return null;
    try {
      const previewControls = deriveLoomParameterControls(preview.document);
      const control = previewControls.find((candidate) => (
        candidate.id === preview.controlId
      ));
      if (typeof control?.value !== "number") return null;
      const acceptedControls = deriveLoomParameterControls(authoringDocument, "wing");
      const acceptedValue = (path: string) => acceptedControls.find((candidate) => (
        formatLoomControlPath(candidate.locator.path) === path
      ))?.value;
      const target = mapLoomAnalyticTargetValue(
        asset,
        preview.analyticJacobianParameterId,
        control.value,
        acceptedValue("panels[panel-1].chordRootM") as number | undefined,
        acceptedValue("panels[panel-1].chordTipM") as number | undefined,
      );
      return target === null
        ? null
        : deriveLoomAnalyticPreviewSurface(asset, preview.analyticJacobianParameterId, target);
    } catch {
      return null;
    }
  }, [asset, authoringDocument, explicitTrimPreview, geometryCurrent, preview]);
  const previewBackend = gpuUnionActive ? "gpu-explicit-fragment-classification" : provisionalUnionAvailable
    ? "provisional-explicit-classification"
    : !explicitTrimAvailable ? "none"
    : preview && analyticPreviewSurface
      ? "analytic-jacobian-linearization"
      : explicitTrimPreview.method === "frozen-topology-jacobian-linearization"
        ? "symmetric-explicit-trim-linearization"
        : "symmetric-explicit-trim-base";
  const previewAuthoringCommand: typeof previewCommand = (command, request) => previewCommand(command, {
    ...request,
    analyticJacobianParameterId: request.analyticJacobianParameterId
      && asset?.header.jacobian.parameterOrder.includes(request.analyticJacobianParameterId)
      ? request.analyticJacobianParameterId
      : null,
  });
  const recordDefinition = async () => {
    if (authoringReadOnlyReason) {
      setRecordState({ status: "error", message: authoringReadOnlyReason });
      return;
    }
    if (!persistedProject) {
      setRecordState({ status: "unavailable" });
      return;
    }
    const requestId = recordRequestRef.current + 1;
    recordRequestRef.current = requestId;
    recordAbortRef.current?.abort();
    const controller = new AbortController();
    recordAbortRef.current = controller;
    setRecordState({ status: "recording" });
    try {
      const cachedIdentity = recordIdentityRef.current?.projectId === projectId
        ? recordIdentityRef.current.identity
        : null;
      const identity = cachedIdentity ?? await getOrCreateLoomAuthoringRecordIdentity(
          projectId,
          `Loom geometry · ${projectName}`.slice(0, 300).trim(),
        );
      if (recordRequestRef.current !== requestId || controller.signal.aborted) return;
      recordIdentityRef.current = { projectId, identity };
      const result = await recordLoomAuthoringDefinition({
        projectId,
        logicalId: identity.logicalId,
        targetName: identity.targetName,
        document: authoringDocument,
        head: history.activeHead,
      }, controller.signal);
      if (recordRequestRef.current !== requestId || controller.signal.aborted) return;
      setRecordState({
        status: "recorded",
        artifactVersionId: result.artifact_version_id,
        replayed: result.replayed,
      });
    } catch (error) {
      if (recordRequestRef.current !== requestId || controller.signal.aborted) return;
      setRecordState({
        status: "error",
        message: error instanceof Error ? error.message : "Unable to record the Loom definition",
      });
    }
  };
  const changeViewportPresentation = (next: LoomViewportPresentationState) => {
    setViewportPresentation(next);
    setLayers((current) => ({ ...current, ...loomLayersForSurfaceStyle(next.surfaceStyle) }));
  };
  const reviewRebuildBlockers = (blockers: readonly LoomRebuildBlocker[]) => {
    const target = blockers.find((blocker) => blocker.controlPath)
      ?? blockers.find((blocker) => blocker.nodeId);
    if (target?.nodeId && authoringDocument.nodes.some((node) => node.id === target.nodeId)) {
      selectNode(target.nodeId);
    }
    setRebuildFocusPath(target?.controlPath ?? null);
  };
  return (
    <LoomControlSurfaceProvider document={authoringDocument} onCommand={applyAuthoringCommand}
      projectId={persistedProject ? projectId : LOOM_LOCAL_DEMO_PROJECT_ID} selectedNodeId={effectiveSelectedNodeId}>
      <LoomControlRuntimeObserver onChange={setControlRuntime} />
      <LoomControlSelectionBridge intent={controlSelectionIntent}
        onSettled={settleControlSelectionIntent} />
      <main
      className="pg-workbench is-loom-viewport-first"
      data-audit-sequence={history.operations.length}
      data-accepted-scene-root-id={`loom-scene:${projectId}`}
      data-content-state-id={history.activeHead.stateId}
      data-definition-revision={authoringDocument.revision}
      data-head-frame-id={history.operations[history.operations.length - 1]?.operationId ?? "bootstrap"}
      data-history-storage-status={storageStatus.kind}
      data-geometry-current={String(geometryCurrent)}
      data-accepted-assembly-revision={acceptedAssembly?.asset.header.cacheReceipt.sceneRevision ?? "none"}
      data-workspace-audit-sequence={workspaceHistory.operations.length}
      data-workspace-content-state-id={workspaceHistory.activeHead.stateId}
      data-preview-active={String(previewActive)}
      data-preview-analytic-parameter={analyticPreviewSurface?.parameterId ?? "none"}
      data-preview-backend={previewBackend}
      data-preview-base-revision={preview?.baseRevision ?? authoringDocument.revision}
      data-preview-control-id={preview?.controlId ?? "none"}
      data-preview-jacobian-delta={analyticPreviewSurface?.deltaJacobian ?? "none"}
      data-preview-target-value={analyticPreviewSurface?.targetValue ?? "none"}
      data-product-selection-kind={productSelection.kind}
      data-rebuild-accepted-revision={authoritativeRebuild.acceptedRevision ?? "none"} data-rebuild-accepted-state-id={authoritativeRebuild.acceptedStateId ?? "none"}
      data-rebuild-current-revision={authoritativeRebuild.activeRevision ?? "none"}
      data-rebuild-focus-path={rebuildFocusPath ?? "none"} data-rebuild-phase={authoritativeRebuild.phase}
      data-rebuild-queued-revision={authoritativeRebuild.queuedRevision ?? "none"} data-rebuild-transport-active={String(authoritativeRebuild.transportActive)}
      data-rebuild-worker-phase={authoritativeRebuild.progress?.phase ?? "none"}
      data-selected-node-id={effectiveSelectedNodeId}
      data-branch={branch}
      data-surface-appearance={effectiveSurfaceAppearance}
      data-testid="loom-native-hybrid-workbench"
    >
      <LoomPanelLayout storageId="native" taskDriven viewportFirst>
        <aside
          aria-label="Aircraft tree"
          className="pg-panel pg-controls"
          data-dock="left"
          data-open="true"
          data-pane="tree"
          data-layout-mode="desktop-pinned-responsive-overlay"
          data-testid="loom-pane-pattern"
          id="loom-pattern-pane"
        >
          <LoomTaskTreePanel advancedAuthoring={<LoomTaskAdvancedAuthoring
            asset={asset} authoring={authoringRuntime} definitionPreviewActive={definitionPreviewActive}
            explicitTrimPreview={explicitTrimPreview} extractionDirty={effectiveExtractionDirty}
            geometryCurrent={geometryCurrent} gpuUnionActive={gpuUnionActive}
            jacobianScale={jacobianScale} layers={layers} onJacobianScaleChange={setJacobianScale}
            onLayersChange={setLayers} onParameterChange={setParameterId}
            onPreviewCommand={previewAuthoringCommand} onRecordDefinition={() => void recordDefinition()}
            onSelect={selectNode} onViewportPresentationChange={setViewportPresentation}
            parameterId={parameterId} persistedProject={persistedProject} projectId={projectId}
            provisionalUnionAvailable={provisionalUnionAvailable}
            provisionalUnionPreview={provisionalUnionPreview} recordState={recordState}
            selectedNodeId={effectiveSelectedNodeId} viewportPresentation={viewportPresentation} />}
            aircraftName={projectName}
            document={authoringDocument}
            onCommand={applyAuthoringCommand}
            onOpenSection={openSection}
            onSelect={selectNode} onSelectControl={selectControl}
            sectionDesigns={productSectionDesigns} selection={productSelection} />
        </aside>
        <LoomTaskPropertiesPanel document={authoringDocument}
          onCommand={applyAuthoringCommand} onOpenSection={() => openSection(productSelection.nodeId)}
          onPreviewCancel={cancelPreview} onPreviewCommand={previewAuthoringCommand}
          onSelect={selectNode} sectionDesigns={productSectionDesigns} selection={productSelection} />
        <section className="pg-panel pg-stage" aria-label="Aircraft geometry" data-testid="loom-workbench-stage">
          <div className="pg-stage-bar loom-native-stage-bar">
            <div>
              <span className={`pg-status ${explicitTrimAvailable ? "is-ready" : loadError ? "is-error" : ""}`} data-testid="loom-native-status">
                <i />{asset
                  ? definitionPreviewActive ? previewActive ? "Editing preview"
                    : authoritativeRebuild.transportActive ? "Updating aircraft geometry…"
                    : authoritativeRebuild.phase === "error" ? "Update failed — last accepted shown"
                    : "Aircraft change pending" : "Geometry ready"
                  : loadError ? "Geometry unavailable" : "Loading geometry"}
              </span>
              <small>{loadError ?? (definitionPreviewActive
                ? previewActive ? "Release the control to evaluate this change."
                  : authoritativeRebuild.transportActive ? "The accepted aircraft stays visible while the geometry engine evaluates the change."
                  : "The change has not replaced the accepted aircraft."
                : "Select a component in the tree or directly in the scene.")}</small>
            </div>
            <div className="pg-native-legend" aria-label="Hybrid geometry legend">
              <span><i className="is-surface" />Surface</span>
              <span><i className="is-parameter" />Section lines</span>
              <span><i className="is-seam" />Join</span>
              <span><i className="is-te" />Trailing edge</span>
            </div>
            <div className="loom-stage-presentation-tools">
              <LoomAuthoritativeRebuildControl onRebuild={authoritativeRebuild.rebuild}
                onReviewBlockers={reviewRebuildBlockers}
                runtime={authoritativeRebuild} serviceStatus={authoritativeRebuild.serviceStatus} />
              <LoomViewportPresentationControl onCameraFitRequest={() => setCameraFitRequest((request) => request + 1)} onChange={changeViewportPresentation} state={viewportPresentation} surfaceKind={inspectionSurfaceKind} />
              <LoomSurfaceAppearanceControl appearance={effectiveSurfaceAppearance} onChange={setSurfaceAppearance} surfaceKind={inspectionSurfaceKind} />
            </div>
          </div>
          {asset ? (
            <LoomNativeHybridViewer
              acceptedAssemblySurface={acceptedAssembly?.asset.displaySurface ?? null}
              analyticPreviewSurface={analyticPreviewSurface}
              asset={asset}
              authoringDocument={displayedAuthoringDocument}
              authoringPreview={definitionPreviewActive}
              constructionPhase={authoritativeRebuild.progress?.phase ?? null} constructionTrace={authoritativeRebuild.progress?.construction_trace ?? null}
              cameraFitRequest={cameraFitRequest}
              explicitTrimPreview={explicitTrimPreview}
              gpuUnionPlan={gpuUnionPlan}
              {...(definitionPreviewActive ? {
                authoringPreviewPhase: previewActive ? "transient" as const : "dirty" as const,
              } : {})}
              jacobianParameterId={parameterId}
              jacobianScale={jacobianScale}
              layers={layers}
              onSelectAuthoringNode={selectNode}
              provisionalUnionPreview={provisionalUnionPreview}
              provisionalUnionRuntime={provisionalUnionRuntime}
              selectedAuthoringNodeId={effectiveSelectedNodeId}
              surfaceAppearance={effectiveSurfaceAppearance}
              viewportPresentation={viewportPresentation}
            />
          ) : (
            <div className="pg-stage-empty">
              <strong>{loadError ? "Aircraft geometry failed to load" : "Loading aircraft geometry"}</strong>
              <span>{loadError ?? "Preparing the aircraft view…"}</span>
            </div>
          )}
          <footer className="pg-stage-footer">
            <span>{definitionPreviewActive ? authoritativeRebuild.transportActive
              ? "Updating geometry; the last accepted aircraft remains visible."
              : "The current edit is not applied to accepted geometry."
              : "Accepted aircraft geometry. Select a component to edit it."}</span>
            <details data-technical-details><summary>Technical details</summary>
              <code>{gpuUnionActive ? "implicit fragment classification · visual only"
                : provisionalUnionAvailable ? "provisional explicit union · open join"
                : assemblyCurrent ? "accepted fuselage + starboard + port occurrence surface" : "accepted reflected pairwise-hybrid triangle representation"}</code>
            </details>
          </footer>
        </section>
        <footer className="loom-native-task-status" aria-live="polite" data-testid="loom-task-status">
          <span>{previewActive ? "Editing preview" : effectiveExtractionDirty
            ? authoritativeRebuild.transportActive ? "Updating geometry" : "Aircraft change pending" : "Ready"}</span>
          <button aria-label="Undo" disabled={history.cursor === 0} onClick={undo} type="button">Undo</button>
          <button aria-label="Redo" disabled={history.cursor >= history.activeBranchOperationIds.length} onClick={redo} type="button">Redo</button>
          <button onClick={() => onTaskRequest?.("details")} type="button">Review details</button>
          <button aria-busy={recordState.status === "recording"} className="is-primary"
            disabled={!persistedProject || recordState.status === "recording"}
            onClick={() => void recordDefinition()} type="button">Save version</button>
          {recordState.status === "recorded" ? <small role="status">{sectionDesigns.length
            ? "Aircraft version saved; session sections not included" : "Version saved"}</small>
            : recordState.status === "error" ? <small role="alert">{recordState.message}</small>
            : recordState.status === "unavailable" ? <small>Save requires a Thread project</small>
            : sectionDesigns.length ? <small>Section designs are session-only and are not included in Save version</small> : null}
        </footer>
      </LoomPanelLayout>
      </main>
    </LoomControlSurfaceProvider>
  );
}
